/**
 * Co-pilot contextual explainers (Phase 5, Feb 2026).
 *
 * Each entry in this map is the read-only copy shown in the Co-pilot side
 * panel for the matching wizard step. Keep these short and warm — the
 * user already sees the step's primary headline, so the explainer adds
 * the "why" and a couple of "what good looks like" tips.
 *
 * Schema:
 *   {
 *     title:    string,       // 4-6 word heading
 *     summary:  string,       // 1-2 sentence elevator pitch
 *     tips:     string[],     // bullet tips, 1 line each
 *     examples: string[],     // optional concrete "say it like" examples
 *   }
 */
export const COPILOT_EXPLAINERS = {
    project: {
        title: 'Goal · set the basics',
        summary: 'Tell us the rough size and shape of the video so every later step pre-fills sensibly. You can change all of this later.',
        tips: [
            'Name it for your future self ("Q1 launch teaser" beats "untitled 3").',
            'Pick the duration closest to your platform: 30s for IG/TikTok, 60s for YouTube pre-roll.',
            '16:9 for landscape platforms like YouTube, 9:16 for Reels/TikTok/Shorts.',
        ],
    },
    script: {
        title: 'Story · what is this video about?',
        summary: 'Choose one of the four modes. The fastest path: "I&apos;m not sure — give me ideas." We&apos;ll suggest 3 concepts after you add your brand name on the next step.',
        tips: [
            'Already have a script? Pick "I have a script" and paste it — no AI involved.',
            'Got rough notes? Pick "I have a rough idea" and we&apos;ll polish them into a script.',
            'Want to record? Pick "Record/Upload narration" and we&apos;ll transcribe + structure it.',
        ],
        examples: [
            '"Promote our new ergonomic chair to remote workers in India"',
            '"Funny 30s ad for our hot-sauce, targeting Gen Z"',
        ],
    },
    concept: {
        title: 'Concept · pick a direction',
        summary: 'Add your brand name, optionally drop in a website / social handle / reference video — then we&apos;ll suggest 3 tailored concepts. Pick the one that fits.',
        tips: [
            'Adding a reference (URL / Instagram / TikTok) makes the concepts ~3× more on-brand.',
            'You can pick a concept, edit it, OR start over — no penalty.',
            'Each concept becomes a script in the next step — automatically.',
        ],
    },
    characters: {
        title: 'Cast & World · who appears?',
        summary: 'We&apos;ve auto-extracted the characters, props and (if your script has narration) the narrator. Add photos, costumes, or descriptions so every shot is consistent.',
        tips: [
            'Click a character row to expand it. Generate / Upload / Library — all visible.',
            'A photo locks the face across the whole video. No photo = AI picks each time.',
            'Try-On combines an avatar with a costume image — great for product try-ons.',
        ],
    },
    branding: {
        title: 'Branding · logo & outro',
        summary: 'Drop your logo on every frame and add a short outro card. Both are optional — hit "Skip branding" if you don&apos;t need them.',
        tips: [
            'Logo top-right works for most platforms (avoids YouTube\'s bottom UI).',
            'Outro of 2-3s with your handle/CTA boosts follower conversion ~25%.',
            'You can change the outro later in the Studio — this isn&apos;t a final commitment.',
        ],
    },
    tier: {
        title: 'Quality · pick a tier',
        summary: 'Three tiers, same workflow, different model quality. Standard is the sweet spot for most ads. Premium is for hero launches.',
        tips: [
            'Economy = great for drafts and internal reviews.',
            'Standard = our default. Best balance of quality and credits.',
            'Premium = top-tier video models (Veo 3, Sora). Use for paid social.',
        ],
    },
    storyboard: {
        title: 'Storyboard · panel-by-panel',
        summary: 'Each shot becomes 4-6 panels. Edit any panel image, swap props, or rewrite a panel description — then generate the video.',
        tips: [
            'Edit the panel description first; image regenerates automatically.',
            'Auto-Pilot generates EVERYTHING in sequence. Use it for first drafts.',
            'Right-click a panel for "lock", "regenerate", or "extend duration".',
        ],
    },
    studio: {
        title: 'Studio · final cut',
        summary: 'Arrange clips, add music, add captions. This is the last stop before export — and most of it is drag-and-drop.',
        tips: [
            'Click any clip to trim. Drag the edges to extend.',
            'Tracks are layered — top track wins for visual; lower tracks are audio.',
            'Export in 16:9, 9:16 or 1:1 — we re-frame intelligently.',
        ],
    },
    dashboard: {
        title: 'Your projects',
        summary: 'Every video you start lives here. Click one to resume right where you left off.',
        tips: [
            'Projects autosave every few seconds — you can close the tab anytime.',
            'Use "Duplicate" to A/B test variations of the same script.',
        ],
    },
};

export const getExplainer = (step) => COPILOT_EXPLAINERS[step] || null;
